const isDevelop = () => {
  return process.env.REACT_APP_MODE === 'develop'
}

const isUUID = (value: string) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return typeof value === 'string' && uuidRegex.test(value)
}

const numberFormatter = Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatNumber = (value: number) => {
  return numberFormatter.format(value)
}

const getLuminance = (color: string) => {
  const rgb = color.match(/\w\w/g)?.map((c) => parseInt(c, 16))

  if (!rgb || rgb.length < 3)
    throw new Error(`Invalid color provided: ${color}`)

  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]
}

const getTextColor = (bgColor: string) => {
  const luminance = getLuminance(bgColor)
  return luminance > 128 ? '#04002E' : '#FFFFFF'
}

const getRandomColor = () => {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }

  return color
}

const calculateAge = (dateAndTime: string) => {
  const now = new Date().getTime()
  const createdDate = new Date(dateAndTime).getTime()
  const diffInSeconds = Math.floor((now - createdDate) / 1000)
  let age: string

  if (diffInSeconds < 60) {
    age = `${diffInSeconds}s`
  } else if (diffInSeconds < 3600) {
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    age = `${diffInMinutes}m`
  } else if (diffInSeconds < 86400) {
    const diffInHours = Math.floor(diffInSeconds / 3600)
    age = `${diffInHours}h`
  } else {
    const diffInDays = Math.floor(diffInSeconds / 86400)
    age = `${diffInDays}d`
  }

  return age
}

const getEnums = <T extends { [key: string]: number | string }>(
  enumType: T,
  exclude: T[keyof T][] = [],
): Array<[key: keyof T, value: T[keyof T]]> => {
  const keys = Object.keys(enumType).filter(
    (key) =>
      isNaN(Number(key)) && !exclude.includes(enumType[key] as T[keyof T]),
  )

  return keys.map((key) => [key, enumType[key] as T[keyof T]])
}

const getFileExtension = (fileName: string) => {
  return fileName.split('.').pop()?.toLowerCase()
}

const getFileNameWithoutExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.')
  return lastDotIndex === -1 ? fileName : fileName.slice(0, lastDotIndex)
}

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms))

const getEnergyEfficiencyTextColor = (value: number) => {
  if (value >= 91) {
    return '#00EB79'
  }

  if (value >= 81) {
    return '#00D06C'
  }

  if (value >= 71) {
    return '#00B25C'
  }

  if (value >= 61) {
    return '#00914B'
  }

  if (value >= 51) {
    return '#FFBC27'
  }

  if (value >= 41) {
    return '#E49D01'
  }

  if (value >= 31) {
    return '#FF0004'
  }

  if (value >= 21) {
    return '#D80004'
  }

  if (value >= 11) {
    return '#AF0003'
  }

  return '#700102'
}

const ordinalDigits = [
  'zeroth',
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
]
const pr = new Intl.PluralRules('en-US', { type: 'ordinal' })
const suffixes = new Map([
  ['one', 'st'],
  ['two', 'nd'],
  ['few', 'rd'],
  ['other', 'th'],
])

const formatOrdinals = (value: number, numberWithSuffix?: boolean) => {
  if ((value >= 0 || value < 10) && !numberWithSuffix) {
    return ordinalDigits[value]
  }

  const rule = pr.select(value)
  const suffix = suffixes.get(rule)
  return `${value}${suffix}`
}

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

const formatCurrency = (cents: number, locale = 'en-US', currency = 'USD') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

const splitS3FilePath = (filepath: string) => {
  const splitted = filepath.split('/')
  return {
    s3_folder: splitted[0] ?? '',
    s3_file: filepath,
  }
}

const equalsWithEpsilon = (a: number, b: number, epsilon: number = 1e-3) => {
  return Math.abs(a - b) < epsilon
}

export {
  delay,
  isDevelop,
  isUUID,
  capitalize,
  formatOrdinals,
  formatNumber,
  getTextColor,
  getRandomColor,
  calculateAge,
  getEnums,
  getFileExtension,
  getFileNameWithoutExtension,
  getEnergyEfficiencyTextColor,
  formatCurrency,
  splitS3FilePath,
  equalsWithEpsilon,
}
